import { useEffect, useRef } from "react";

/**
 * Mantiene el navegador "activo" mientras `active` sea true para evitar el
 * throttling agresivo de pestañas en segundo plano (Chrome reduce timers a
 * ~1/seg y aplaza tareas cuando la pestaña está oculta).
 *
 * Estrategia:
 * 1) Reproduce un buffer de audio silencioso en bucle vía Web Audio API.
 *    Chrome/Edge/Firefox no throttlean pestañas que están emitiendo audio.
 * 2) Solicita Screen Wake Lock cuando esté disponible (evita suspensión
 *    del equipo). Es best-effort; si falla, se ignora silenciosamente.
 *
 * El audio requiere gesto de usuario previo; como el procesado siempre se
 * dispara con un click, el AudioContext se puede iniciar sin problemas.
 */
export function useKeepAwake(active: boolean) {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active) return;

    // ── Silent audio loop ─────────────────────────────
    let cancelled = false;
    try {
      const Ctx: typeof AudioContext | undefined =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * 1, ctx.sampleRate);
        // buffer vacío (silencio); no rellenamos nada
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.loop = true;
        const gain = ctx.createGain();
        gain.gain.value = 0.0001; // prácticamente silencio pero suficiente para que el navegador considere la pestaña "audible"
        src.connect(gain).connect(ctx.destination);
        src.start();
        sourceRef.current = src;
        gainRef.current = gain;
        if (ctx.state === "suspended") {
          void ctx.resume().catch(() => {});
        }
      }
    } catch {
      // Ignorar: keep-awake es best-effort
    }

    // ── Screen Wake Lock ──────────────────────────────
    const requestLock = async () => {
      try {
        const nav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<WakeLockSentinel> } };
        if (nav.wakeLock?.request) {
          const lock = await nav.wakeLock.request("screen");
          if (cancelled) {
            void lock.release().catch(() => {});
          } else {
            wakeLockRef.current = lock;
          }
        }
      } catch {
        // ignorar
      }
    };
    void requestLock();

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        void requestLock();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      try { sourceRef.current?.stop(); } catch { /* noop */ }
      try { void audioCtxRef.current?.close(); } catch { /* noop */ }
      sourceRef.current = null;
      gainRef.current = null;
      audioCtxRef.current = null;
      if (wakeLockRef.current) {
        void wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [active]);
}
